import { ReactNode, useEffect } from "react";
import { initials, timeLeft, useNow, fmtDateTime } from "../lib/format";
import { Icon } from "./Icons";
import { useMagnetic } from "../lib/anim";

/* ---- Avatar --------------------------------------------------------- */
export function Avatar({ name, hue = 20, size = 34 }: { name: string; hue?: number; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: size, flexShrink: 0,
      display: "grid", placeItems: "center",
      background: `linear-gradient(140deg, hsl(${hue} 42% 62%), hsl(${hue + 30} 40% 46%))`,
      color: "#fff", fontSize: size * 0.36, fontWeight: 600, letterSpacing: ".02em",
      boxShadow: "inset 0 0 0 1px rgba(255,255,255,.25)",
    }}>{initials(name)}</div>
  );
}

/* ---- Design plate (generative artwork stand-in) --------------------- */
export function Plate({ hue, h = 150, label, seed = 1 }: { hue: number; h?: number; label?: string; seed?: number }) {
  const g = `grad${hue}-${seed}`;
  return (
    <div style={{ position: "relative", borderRadius: 14, overflow: "hidden", height: h }}>
      <svg width="100%" height="100%" viewBox="0 0 400 260" preserveAspectRatio="xMidYMid slice" style={{ display: "block" }}>
        <defs>
          <linearGradient id={g} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={`hsl(${hue} 34% 82%)`} />
            <stop offset="1" stopColor={`hsl(${hue + 24} 30% 60%)`} />
          </linearGradient>
        </defs>
        <rect width="400" height="260" fill={`url(#${g})`} />
        <g stroke="rgba(255,255,255,.45)" strokeWidth="1" fill="none">
          <path d={`M0 ${120 + seed * 8} H400`} />
          <path d={`M${140 + seed * 20} 0 V260`} />
          <rect x={60 + seed * 10} y={70} width="120" height="120" rx="4" />
        </g>
        <g fill="rgba(30,20,12,.10)">
          <circle cx={300} cy={80} r={38} />
        </g>
      </svg>
      {label && <span style={{ position: "absolute", left: 12, bottom: 10, color: "#fff", fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: ".08em", textShadow: "0 1px 4px rgba(0,0,0,.3)" }}>{label}</span>}
    </div>
  );
}

/* ---- Meter ---------------------------------------------------------- */
export function Meter({ value, thin, color }: { value: number; thin?: boolean; color?: string }) {
  return <div className={"meter" + (thin ? " thin" : "")}><i style={{ width: `${Math.max(0, Math.min(100, value))}%`, background: color }} /></div>;
}

/* ---- Stat card ------------------------------------------------------ */
export function Stat({ label, children, sub, accent, onClick }: { label: string; children: ReactNode; sub?: ReactNode; accent?: boolean; onClick?: () => void }) {
  return (
    <div
      className="card pad reveal hoverable"
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      style={onClick ? { cursor: "pointer" } : undefined}
      title={onClick ? "Open" : undefined}
    >
      <div className="row between" style={{ gap: 8 }}>
        <div className="kicker">{label}</div>
        {onClick && <Icon.arrowUpRight width={13} style={{ color: "var(--ink-3)", flexShrink: 0 }} />}
      </div>
      <div className="serif" style={{ fontSize: 34, marginTop: 8, color: accent ? "var(--accent-ink)" : "var(--ink)" }}>{children}</div>
      {sub && <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* ---- Page header ---------------------------------------------------- */
export function PageHead({ kicker, title, children }: { kicker: string; title: string; children?: ReactNode }) {
  return (
    <div className="row between wrap reveal page-head" style={{ marginBottom: 26, gap: 16 }}>
      <div>
        <div className="kicker">{kicker}</div>
        <h1 className="serif" style={{ fontSize: 38, marginTop: 6 }}>{title}</h1>
      </div>
      {children && <div className="row wrap page-actions" style={{ gap: 10 }}>{children}</div>}
    </div>
  );
}

/* ---- Button with optional magnetic hover ---------------------------- */
export function MagButton({ children, className = "btn", onClick, disabled }: { children: ReactNode; className?: string; onClick?: () => void; disabled?: boolean }) {
  const ref = useMagnetic<HTMLButtonElement>(0.25);
  return <button ref={ref} className={className} onClick={onClick} disabled={disabled}>{children}</button>;
}

/* ---- Modal ---------------------------------------------------------- */
export function Modal({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title?: string; children: ReactNode; wide?: boolean }) {
  useEffect(() => {
    if (!open) return;
    const f = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", f);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", f); document.body.style.overflow = ""; };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} className="modal-wrap" style={{
      position: "fixed", inset: 0, zIndex: 90, display: "grid", placeItems: "center", padding: 20,
      background: "rgba(20,14,8,.42)", backdropFilter: "blur(6px)", animation: "floatIn .25s ease",
    }}>
      <div onClick={(e) => e.stopPropagation()} className={"card modal-card" + (wide ? " wide" : "")} style={{
        width: "100%", maxWidth: wide ? 720 : 480, padding: 26, boxShadow: "var(--shadow-lg)",
        animation: "floatIn .4s var(--ease-out)", maxHeight: "88vh", overflow: "auto",
      }}>
        {title && (
          <div className="row between" style={{ marginBottom: 18 }}>
            <h3 className="serif" style={{ fontSize: 24 }}>{title}</h3>
            <button className="iconbtn" onClick={onClose} aria-label="Close"><Icon.close /></button>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/* ---- Empty state ---------------------------------------------------- */
export function Empty({ children }: { children: ReactNode }) {
  return <div className="panel" style={{ padding: 40, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>{children}</div>;
}

/* ---- Segmented control ---------------------------------------------- */
export function Seg<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[] }) {
  return (
    <div className="row" style={{ gap: 6, background: "var(--paper-2)", padding: 4, borderRadius: 100, border: "1px solid var(--line)" }}>
      {options.map((o) => (
        <button key={o.value} className={"chip" + (value === o.value ? " on" : "")} style={{ border: "none", background: value === o.value ? "var(--ink)" : "transparent" }} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/* ---- Status badge helpers ------------------------------------------- */
export function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { cls: string; label: string }> = {
    open: { cls: "accent", label: "Open for bids" }, draft: { cls: "", label: "Draft" },
    evaluation: { cls: "info", label: "Under evaluation" }, awarded: { cls: "ok", label: "Awarded" },
    closed: { cls: "warn", label: "Bids closed" },
    invited: { cls: "", label: "Invited" }, registered: { cls: "info", label: "Registered" },
    submitted: { cls: "ok", label: "Bid submitted" }, declined: { cls: "danger", label: "Declined" },
    received: { cls: "info", label: "Received" }, shortlisted: { cls: "accent", label: "Shortlisted" },
    unsuccessful: { cls: "", label: "Unsuccessful" },
    compliant: { cls: "ok", label: "Compliant" }, "non-compliant": { cls: "danger", label: "Non-compliant" },
    pending: { cls: "warn", label: "Compliance pending" },
    answered: { cls: "ok", label: "Answered" }, "rfi-open": { cls: "warn", label: "Awaiting answer" },
    all: { cls: "info", label: "All bidders" }, private: { cls: "", label: "Private" },
    sealed: { cls: "warn", label: "Sealed" },
  };
  const m = map[status] ?? { cls: "", label: status };
  return <span className={"badge " + m.cls}><span className="dot" />{m.label}</span>;
}

/* ---- Live countdown -------------------------------------------------- */
const pad = (n: number) => String(n).padStart(2, "0");

export function Countdown({ to, small, closedLabel = "Bids closed" }: { to: string; small?: boolean; closedLabel?: string }) {
  const now = useNow(1000);
  const t = timeLeft(to, now);
  if (t.past) {
    return (
      <div className="row" style={{ gap: 10 }}>
        <span className="badge warn" style={{ padding: "8px 16px", fontSize: 12.5 }}><Icon.lock width={14} />{closedLabel}</span>
        <span className="dim" style={{ fontSize: 12.5 }}>{fmtDateTime(to)}</span>
      </div>
    );
  }
  const hot = t.totalMs < 48 * 3600 * 1000;
  const units: [number | string, string][] = [[t.d, "days"], [pad(t.h), "hrs"], [pad(t.m), "min"], [pad(t.s), "sec"]];
  return (
    <div className={"cd" + (hot ? " hot" : "") + (small ? " sm" : "")} aria-label={`Time to close: ${t.d} days ${t.h} hours ${t.m} minutes`}>
      {units.map(([n, l]) => (
        <div className="unit" key={l}>
          <div className="n num">{n}</div>
          <div className="l">{l}</div>
        </div>
      ))}
    </div>
  );
}
